/**
 * Resolving a cleaning type from whatever the wire happens to carry.
 *
 * The legacy contract transports the cleaning type as its *price*
 * (`cleaning_type: "128"`) and recovers the name with
 * `switch (Number(value)) { case table.standard: ... default: throw }`.
 * That has three failure modes this module removes:
 *
 *   1. a browser holding a cached bundle sends last week's price and gets a
 *      500 `invalid cleaning type` at checkout;
 *   2. a decimal price matches no case and throws;
 *   3. two cleaning types can never share a price.
 *
 * `resolveCleaningType` accepts ids, legacy names, and price strings from *any*
 * rate card, so old clients keep working while new ones send ids. It only fails
 * when the value genuinely identifies nothing.
 */

import {
  CLEANING_TYPE_IDS,
  CLEANING_TYPE_LABELS,
  CLEANING_TYPE_LEGACY_NAMES,
  CleaningTypeId,
  RATE_CARDS,
  getRateCard,
} from "./rates";
import { dollarsToCents } from "./money";

const BY_LEGACY_NAME = new Map<string, CleaningTypeId>();
for (const id of CLEANING_TYPE_IDS) {
  BY_LEGACY_NAME.set(id, id);
  BY_LEGACY_NAME.set(CLEANING_TYPE_LEGACY_NAMES[id].toLowerCase(), id);
  BY_LEGACY_NAME.set(CLEANING_TYPE_LABELS[id].toLowerCase(), id);
}

/**
 * Every price any rate card has ever charged, mapped to its cleaning type.
 *
 * Built across all cards on purpose: a client that sends a price from an older
 * card is out of date, not wrong, and should be resolved rather than rejected.
 *
 * This map is a *fallback only*, because prices are genuinely ambiguous across
 * cards — $228 was V1 Super Clean and later V2 Move In / Out, and $248 was V2
 * Super Clean and later V3 Move In / Out. Resolving by price alone would
 * relabel historical Super Clean bookings as Move In / Out, which also flips
 * their free-extras rule. Always prefer the card in force for the booking.
 */
const BY_PRICE_CENTS = new Map<number, CleaningTypeId>();
for (const card of RATE_CARDS) {
  for (const id of CLEANING_TYPE_IDS) {
    BY_PRICE_CENTS.set(card.cleaningTypes[id], id);
  }
}

export class UnknownCleaningTypeError extends Error {
  constructor(value: unknown) {
    super(
      `Could not resolve a cleaning type from ${JSON.stringify(value)}. ` +
        `Expected one of ${CLEANING_TYPE_IDS.join(", ")}, a legacy name, or a known price.`
    );
    this.name = "UnknownCleaningTypeError";
  }
}

/**
 * Resolve a cleaning type from an id, a legacy name, or a price.
 *
 * Returns `null` rather than throwing so callers can decide whether an
 * unresolvable value is a validation error or a 500. Use
 * `resolveCleaningTypeOrThrow` when it must be present.
 */
export function resolveCleaningType(
  value: string | number | null | undefined,
  createdAt?: Date
): CleaningTypeId | null {
  if (value === null || value === undefined) return null;

  const raw = String(value).trim();
  if (!raw) return null;

  const byName = BY_LEGACY_NAME.get(raw.toLowerCase());
  if (byName) return byName;

  // A bare number is a price, in dollars, in the legacy wire format.
  const asNumber = Number(raw.replace(/[$,]/g, ""));
  if (!Number.isFinite(asNumber)) return null;

  const cents = dollarsToCents(asNumber);

  // Prefer the card the booking was actually sold under. Only fall back to the
  // all-time map for a value that card does not recognise — that is a stale
  // client, which should still resolve rather than 500 at checkout.
  if (createdAt) {
    const card = getRateCard(createdAt);
    for (const id of CLEANING_TYPE_IDS) {
      if (card.cleaningTypes[id] === cents) return id;
    }
  }

  return BY_PRICE_CENTS.get(cents) ?? null;
}

export function resolveCleaningTypeOrThrow(
  value: string | number | null | undefined,
  createdAt?: Date
): CleaningTypeId {
  const resolved = resolveCleaningType(value, createdAt);
  if (!resolved) throw new UnknownCleaningTypeError(value);
  return resolved;
}

/** The legacy lowercase name, for wire formats and DB rows that still expect it. */
export function toLegacyName(id: CleaningTypeId): string {
  return CLEANING_TYPE_LEGACY_NAMES[id];
}

/** The display label. */
export function toLabel(id: CleaningTypeId): string {
  return CLEANING_TYPE_LABELS[id];
}

/**
 * Whether a cleaning type is at or above the Move In / Out price on its card.
 *
 * This is the "extras are included" rule. It used to be written as a bare
 * numeric comparison against a truncated price (`parseInt(price) >= 273`),
 * which silently stops working the moment the threshold carries cents — the
 * customer is then charged up to $105 for extras that should be free, with no
 * error anywhere. Comparing resolved types on the same card cannot drift.
 */
export function includesFreeExtras(
  cleaningType: CleaningTypeId,
  createdAt: Date
): boolean {
  const card = getRateCard(createdAt);
  return card.cleaningTypes[cleaningType] >= card.cleaningTypes.move_in_out;
}
